import {
  collectModuleRedirectEntries,
  collectModuleRouteEntries,
  getFirstDescendantNavigationUrl,
} from "@/common/helpers/route";
import { useNavigationItem } from "@/common/hooks/use-navigation";
import { NotFoundView } from "@/views/common/notfound.view";
import { useMemo } from "react";
import { Redirect, Route, Switch } from "wouter";

export type ModuleRouterProps = {
  moduleId: string;
  fallbackRedirect?: string;
  notFoundRedirect?: string;
};

function ensureAbsolutePath(path: string) {
  if (path.startsWith("/") && !path.startsWith("~")) {
    return `~${path}`;
  }
  return path;
}

export function ModuleRouter({ moduleId, fallbackRedirect = "/forbidden", notFoundRedirect }: ModuleRouterProps) {
  const moduleNavigation = useNavigationItem(moduleId);
  const redirectTarget = ensureAbsolutePath(getFirstDescendantNavigationUrl(moduleNavigation) ?? fallbackRedirect);
  const routeEntries = useMemo(() => collectModuleRouteEntries(moduleNavigation), [moduleNavigation]);
  const redirectEntries = useMemo(() => collectModuleRedirectEntries(moduleNavigation), [moduleNavigation]);

  return (
    <Switch>
      <Route path="/" component={() => <Redirect to={redirectTarget} replace />} />
      {redirectEntries.map((entry) => (
        <Route
          key={entry.path}
          path={entry.path}
          component={() => <Redirect to={ensureAbsolutePath(entry.target)} replace />}
        />
      ))}
      {routeEntries.map((entry) => (
        <Route key={entry.path} path={entry.path} component={entry.component} nest={entry.nest} />
      ))}
      {renderFallbackRoute(notFoundRedirect)}
    </Switch>
  );
}

function renderFallbackRoute(notFoundRedirect?: string) {
  if (notFoundRedirect) {
    return <Route component={() => <Redirect to={ensureAbsolutePath(notFoundRedirect)} replace />} />;
  }

  return <Route component={NotFoundView} />;
}
